import { Box, Tooltip, Typography } from '@mui/material';
import type React from 'react';
import { type Column, StandardTable } from '../common/UI/StandardTable';
import type { SupportDoc } from './types';
import { formatUploadedDate } from './utils';

interface SupportDocumentTableProps {
  title?: React.ReactNode;
  documents: SupportDoc[];
  emptyMessage?: string;
  renderNameExtras?: (doc: SupportDoc) => React.ReactNode;
  renderActions?: (doc: SupportDoc) => React.ReactNode;
  renderUnderName?: (doc: SupportDoc) => React.ReactNode;
}

const SupportDocumentTable: React.FC<SupportDocumentTableProps> = ({
  title,
  documents,
  emptyMessage = 'No documents uploaded yet.',
  renderNameExtras,
  renderActions,
  renderUnderName,
}) => {
  const columns: Column<SupportDoc>[] = [
    {
      id: 'name',
      label: 'Name',
      render: (doc) => (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {renderNameExtras?.(doc)}
            <Tooltip title={doc.originalFilename}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  wordBreak: 'break-word',
                  fontSize: '1rem',
                  maxWidth: 520,
                  display: 'block',
                  whiteSpace: 'normal',
                }}
              >
                {doc.originalFilename}
              </Typography>
            </Tooltip>
          </Box>
          {renderUnderName?.(doc)}
        </>
      ),
    },
    {
      id: 'date',
      label: 'Date',
      render: (doc) => (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
          {formatUploadedDate(doc.filledAt || doc.uploadedAt)}
        </Typography>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      render: (doc) => <>{renderActions?.(doc)}</>,
    },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      {title && (
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '1rem' }}>
          {title}
        </Typography>
      )}
      <Box
        sx={{
          '& th': {
            backgroundColor: 'greyContrast',
            color: 'primary.contrastText',
            fontWeight: 600,
          },
        }}
      >
        <StandardTable
          columns={columns}
          data={documents}
          keyExtractor={(doc) => doc.originalFilename || ''}
          emptyStateMessage={emptyMessage}
          size="small"
          stickyHeader
          maxHeight={400}
          rowSx={() => ({ '& td, & th': { py: 0.5 } })}
        />
      </Box>
    </Box>
  );
};

export default SupportDocumentTable;
